// Sheet component wrapper
// Re-exports shadcn sheet with customizations

import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  Sheet as ShadcnSheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  sheetVariants,
} from './shadcn/sheet'

const NoOverlayContext = React.createContext(false)

type ShadcnSheetProps = React.ComponentPropsWithoutRef<typeof ShadcnSheet>

interface SheetProps extends ShadcnSheetProps {
  /** Disables the dark overlay, focus trapping, and background scroll lock */
  noOverlay?: boolean
}

function Sheet({ noOverlay = false, ...props }: SheetProps) {
  return (
    <NoOverlayContext.Provider value={noOverlay}>
      <ShadcnSheet modal={!noOverlay} {...props} />
    </NoOverlayContext.Provider>
  )
}

interface SheetContentProps
  extends React.ComponentProps<typeof SheetPrimitive.Content> {
  side?: 'top' | 'bottom' | 'left' | 'right'
  hideCloseButton?: boolean
}

function SheetContent({ side = 'right', hideCloseButton, className, children, ref, ...props }: SheetContentProps) {
  const noOverlay = React.useContext(NoOverlayContext)

  return (
    <SheetPortal>
      {!noOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {!hideCloseButton && (
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
